import React, { useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TextCell } from '@/shared/components/TextCell/TextCell';
import { DataTable, DataTableColumn, ActionsDropdown } from '@/shared';

import { NotificationList, NotificationDataTableProps } from '../../models';

export const NotificationDataTable: React.FC<NotificationDataTableProps> = ({
  users,
  showTenantColumn = false,
  onClickNotification,
  loading = false,
  pagination,
  currentFilters,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onView,
  onFilterChange,
  className,
  filterToolbarSlot,
  filterToolbarActiveCount,
  onClearFilterToolbar,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('notification');

  const renderActionsCell = useCallback(
    (user: NotificationList) => {
      const handleAction = (action: string) => {
        if (action === 'view') {
          onView?.(user);
        }
      };
  
      const actions: {
        id: string;
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        permission?: string;
      }[] = [
        {
          id: 'view',
          label: t('notification.label.details'),
          icon: <Eye size={16} />,
          onClick: () => handleAction('view'),
        },
      ];
  
      return (
        <ActionsDropdown
          actions={actions.filter(action => !action.permission)}
          className='hover:cursor-pointer'
        />
      );
    },
    [onView, navigate]
  );
  
  const renderTitle = (user: NotificationList) => (
    <TextCell value={user.Title} truncate />
  );

  const renderMessage = (user: NotificationList) => (
    <TextCell value={user.Message} truncate />
  );
  const renderDate = (user: NotificationList) => (
    <TextCell value={user.CreatedAt.split('T')[0]} />
  );

  const renderTenantName = (user: NotificationList) => (
    <TextCell
      value={
        user.TenantName != null && String(user.TenantName).trim() !== ''
          ? String(user.TenantName)
          : '—'
      }
      truncate
    />
  );

  const columns: DataTableColumn<NotificationList>[] = useMemo(
    () => [
      {
        id: 'Title',
        header: t('notification.label.title'),
        cell: renderTitle,
        // sortable: true,
        filterable: true,
        accessor: 'Title',
      },
      ...(showTenantColumn
        ? [
            {
              id: 'TenantName',
              header: t('notification.label.tenantName'),
              cell: renderTenantName,
              filterable: true,
              accessor: 'TenantName' as const,
            } satisfies DataTableColumn<NotificationList>,
          ]
        : []),
      {
        id: 'Message',
        header: t('notification.label.message'),
        cell: renderMessage,
        filterable: true,
        accessor: 'Message',
      },

      {
        id: 'CreatedAt',
        header: 'Created At',
        cell: renderDate,
        filterable: true,
        accessor: 'CreatedAt',
      },

      {
        id: 'actions',
        header: t('notification.label.actions'),
        cell: renderActionsCell,
        width: '120px',
      },
    ],
    [renderActionsCell, renderTenantName, showTenantColumn, t]
  );

  const filterDefs = useMemo(
    () => [
      {
        id: 'search',
        label: t('notification.label.search'),
        type: 'text' as const,
        placeholder: t('notification.label.searchNotification'),
      },
    ],
    []
  );

  return (
    <div data-testid="user-data-table" className={className}>
      <DataTable<NotificationList>
        data={users}
        onRowClick={onClickNotification ? (item) => onClickNotification(item) : undefined}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortChange={onSortChange}
        onFilterChange={onFilterChange}
        filters={filterDefs}
        initialFilters={currentFilters}
        filterToolbarSlot={filterToolbarSlot}
        filterToolbarActiveCount={filterToolbarActiveCount}
        onClearFilterToolbar={onClearFilterToolbar}
        showPagination={true}
        showFilters={true}
        emptyMessage={t('notification.noNotification')}
        pageSizeOptions={[5, 10, 20, 50]}
        className="bg-white"
      />
    </div>
  );
};

